import { Dictionary } from 'lodash';
import * as fp from 'lodash/fp';
import { Observable } from '@shared/rx';
import { Primitive } from '@geniuspos/ui-shared';

export type Async<T> = Promise<T> | Observable<T>;
export type SyncOrAsync<T> = T | Async<T>;
export type SyncOrAsyncOrFn<T, TFn extends (...args: any[]) => T = () => T> =
  | SyncOrAsync<T>
  | TFn;
export type DynamicOptionValue<T = string> = SyncOrAsyncOrFn<
  T,
  (formValue: object) => T
>;

/**
 * The interface helps as to avoid typo in fields, when it defined like `{ [key: string]: ... }` and we can't make
 * it a generic.
 *
 * @example
 *
 * In the example we already have next code:
 *
 *   interface IFieldConfig { ... }
 *
 *   interface IMyForm {
 *     name: string;
 *     controls: { [key: string]: IFieldConfig };
 *   }
 *
 *   interface IFormValue {
 *     email: string;
 *     age: number;
 *   }
 *
 *   const form: IMyForm = {       <-- correct definition according to TypeScript
 *     name: 'My Form';
 *     controls: {
 *       'e-mail' : { ... }
 *     };
 *   }
 *
 * There is two problems here:
 * 1. We did a typo in the `email` field name
 * 2. We forgot to add `age` field
 *
 * However, typescript can't show it. You will not see any compile-time errors here.
 *
 * How using this type can fix it? Look at the example.
 *
 *   const form: StrictifyFieldKeys<IMyForm, 'controls', keyof IFormValue> = {
 *     name: 'My Form',
 *     controls: {
 *       'e-mail' : { ... },       <-- TS Error: Property 'e-mail' is missing in type ...
 *                                 <-- TS Error: Property 'age'    is missing in type ...
 *     },
 *   };
 *
 */
export type StrictifyFieldKeys<
  TType extends AnyObject,
  TField extends keyof TType,
  TKeys extends string,
> = TType & {
  [field in TField]: Record<TKeys, TType[TField][string]>;
};

export type SimpleValue = Primitive;
export type NullableSimpleValue = SimpleValue | null;

export type AnyObject = Dictionary<any>;
export type AnyFn = (...args: unknown[]) => unknown;
export type StringObject<T extends string = string> = Dictionary<T>;
export type NumberObject<T extends number = number> = Dictionary<T>;

export type TrackByValue = (index: number, item: any) => string;

export interface PromiseMapOptions {
  concurrency: number;
}

export type ExtractFunctionsUnionFromType<T> = {
  [K in keyof T]: T[K] extends Function ? K : never;
}[keyof T];

// exclude additional keys like [x:string]: any
type KnownKeys<T> = {
  [K in keyof T]: string extends K ? never : number extends K ? never : K;
} extends { [_ in keyof T]: infer U }
  ? U
  : never;

// @ts-ignore
/**
 * Check that field exists in object that return it.
 */
/* eslint-disable */
export const typeField =
  <T>() =>
  <K extends KnownKeys<T>>(field: K): K =>
    field;

export const pathToString: (path: string[]) => string = fp.join('.');

export type ExpendNamespaceTypes<T> = T extends string
  ? T
  : T extends Record<string, any>
    ? {
        [K in keyof T]: ExpendNamespaceTypes<T[K]>;
      }[keyof T]
    : never;

export type RequiredFields<T, K extends keyof T> = T & Required<Pick<T, K>>;

export type RequireOnly<T, K extends keyof T> = Required<Pick<T, K>> &
  Partial<Omit<T, K>>;

/**
 * Enforces mutual exclusivity of properties across plain-object union members.
 * Primitives, functions, and arrays pass through unchanged.
 */
type _UnionObjectKeys<T> = T extends object
  ? T extends (...args: any[]) => any
    ? never
    : T extends any[]
      ? never
      : keyof T
  : never;

type _StrictHelper<T, AllKeys extends PropertyKey> = T extends object
  ? T extends (...args: any[]) => any
    ? T
    : T extends any[]
      ? T
      : T & Partial<Record<Exclude<AllKeys, keyof T>, never>>
  : T;

export type StrictUnion<T> = _StrictHelper<T, _UnionObjectKeys<T>>;
